import React, { useState } from "react";
import { FaArrowRight, FaRegCirclePlay, FaCode, FaRocket, FaUserGroup, FaClock, FaStar } from "react-icons/fa6";
import { BTN_SIZE, Container, H1_SIZE } from "../home/ui";

// Hero artwork (laptop, phone and tech tiles). On desktop (xl+) it sits as a background on the right side
// of the section; on mobile/tablet it shows below the text.
// Put the file at public/img/software-hero.jpg (or change the path). Until it exists, the home hero art is shown.
const heroImg = "/img/hero-bg2.jpg";
const fallbackImg = "/img/hero-bg2.jpg";

// NOTE: keep these numbers accurate for DataMetricks (same figures as the home page hero).
const stats = [
  { icon: FaRocket, value: "100+", label: "Projects Delivered", tone: "text-[#3D5BF6]" },
  { icon: FaUserGroup, value: "50+", label: "Happy Clients", tone: "text-[#7C3AED]" },
  { icon: FaClock, value: "5+", label: "Years Experience", tone: "text-[#6D28D9]" },
  { icon: FaStar, value: "4.9/5", label: "Client Rating", tone: "text-[#F5B301]" },
];

const fadeX = "linear-gradient(to right, transparent 0%, #000 10%, #000 94%, transparent 100%)";
const fadeY = "linear-gradient(to bottom, transparent 0%, #000 8%, #000 92%, transparent 100%)";
const edgeFade = {
  WebkitMaskImage: `${fadeX}, ${fadeY}`,
  WebkitMaskComposite: "source-in",
  maskImage: `${fadeX}, ${fadeY}`,
  maskComposite: "intersect",
};

const SoftHero = () => {
  const [src, setSrc] = useState(heroImg);
  const onError = () => src !== fallbackImg && setSrc(fallbackImg);

  return (
    <section className="relative overflow-hidden bg-gradient-to-r from-[#FCFCFE] via-[#F8F9FF] to-[#F1F4FE] pb-[48px] pt-[110px] xl:flex xl:min-h-[min(38vw,720px)] xl:items-center xl:pb-[40px] xl:pt-[calc(clamp(76px,5.83vw,112px)+24px)]">
      <img
        src={src}
        onError={onError}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-[calc(clamp(76px,5.83vw,112px)+4px)] hidden h-[min(32vw,620px)] w-auto max-w-none select-none xl:block"
        style={edgeFade}
      />

      <Container className="relative grid w-full max-w-[1900px] items-center gap-[32px] xl:grid-cols-[minmax(0,44%)_1fr] xl:pl-[clamp(32px,4.375vw,84px)] xl:pr-[clamp(24px,3.125vw,60px)]">
        <div className="m-hero">
          <span className="inline-flex items-center gap-[8px] rounded-full bg-[#EFEAFF] px-[14px] py-[6px] text-[13px] font-medium text-[#3B2A8F]">
            <FaCode className="text-[13px] text-[#6D28D9]" />
            Software Development
          </span>

          <h1 className={`mt-[18px] font-extrabold tracking-tight text-[#0B0D2C] ${H1_SIZE}`}>
            Transform Your Ideas
            {" "}<br />
            Into Powerful
            {" "}<br />
            <span className="text-gradient">Software Solutions</span>
          </h1>

          <p className="mt-[18px] max-w-[560px] text-[16px] leading-[1.6] text-slate-600 sm:text-[17px] xl:text-[clamp(15px,1.05vw,18px)]">
            We build scalable, secure, and high-performance software solutions tailored to your business needs. From web and
            mobile apps to cloud solutions, we turn your ideas into powerful digital products.
          </p>

          <div className="m-btns mt-[28px] flex flex-wrap items-center gap-[16px]">
            <a
              href="/contact-us"
              className={`group inline-flex items-center rounded-full bg-gradient-to-r from-[#1E6BFF] to-[#5B3DF5] font-medium text-white shadow-[0_14px_30px_-14px_rgba(61,91,246,0.75)] transition hover:brightness-110 ${BTN_SIZE}`}
            >
              Get a Free Consultation
              <FaArrowRight className="text-[13px] transition group-hover:translate-x-1" />
            </a>
            <a
              href="/our-completed-projects"
              className={`inline-flex items-center rounded-full border-[1.5px] border-solid border-[#7FA3FF] bg-white/70 font-medium text-[#1E4DFF] transition hover:bg-[#EEF3FF] ${BTN_SIZE}`}
            >
              <FaRegCirclePlay className="text-[20px]" />
              View Our Work
            </a>
          </div>

          <div className="mt-[32px] grid max-w-[600px] grid-cols-2 gap-[18px] sm:grid-cols-4 sm:gap-[10px]">
            {stats.map((s) => (
              <div key={s.label}>
                <p className="flex items-center gap-[8px] text-[20px] font-bold leading-none text-[#0B0D2C]">
                  <s.icon className={`text-[17px] ${s.tone}`} />
                  {s.value}
                </p>
                <p className="mt-[8px] text-[13px] text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-center xl:hidden">
          <img
            src={src}
            onError={onError}
            alt="Laptop and phone with code, surrounded by technology icons"
            className="h-auto w-full max-w-[760px] select-none object-contain"
            style={edgeFade}
          />
        </div>
      </Container>
    </section>
  );
};

export default SoftHero;
